import { FocusEventHandler, KeyboardEventHandler, forwardRef } from 'react'

import { styled } from 'styled-components'

import Icon from 'core/components/Icon'

import {
  BorrowerSearchInputOptimizer,
  BorrowerSearchInputSubmitHandler,
  SearchPart,
} from './BorrowerSearchInput'
import { BorrowerSearchInputChangeHandler } from './BorrowerSearchInputClearButton'
import BorrowerSearchQuickPrefixButton, {
  StyledToolbarButton,
} from './BorrowerSearchQuickPrefixButton'

export type BorrowerSearchInputSanitizer = (
  searchParts: Array<SearchPart>,
) => string

type BorrowerSearchToolbarProps = {
  isOptimized: boolean
  usedPrefixes: Array<string>
  handleBlur: FocusEventHandler<HTMLDivElement>
  searchParts: Array<SearchPart>
  onChangeSearchInput: BorrowerSearchInputChangeHandler
  optimizeSearchInput: BorrowerSearchInputOptimizer
  sanitizeSearchInput: BorrowerSearchInputSanitizer
  setFocusOnSearchInput: BorrowerSearchInputSubmitHandler
}

const QUICK_PREFIXES = [
  'firstName',
  'lastName',
  'fullName',
  'email',
  'phone',
  'accountId',
  'accountIdPartial',
]

const BorrowerSearchToolbar = forwardRef<
  HTMLDivElement,
  BorrowerSearchToolbarProps
>(
  (
    {
      isOptimized,
      usedPrefixes,
      handleBlur,
      searchParts,
      onChangeSearchInput,
      optimizeSearchInput,
      sanitizeSearchInput,
      setFocusOnSearchInput,
    },
    ref,
  ) => {
    const handleClickPrefix = (prefix: string) => {
      // append the selected prefix to the searchInputValue with same whitespace and a colon
      const sanitizedSearchInputValue = sanitizeSearchInput(searchParts)
      if (sanitizedSearchInputValue.length > 0) {
        onChangeSearchInput(`${sanitizedSearchInputValue} ${prefix}:`)
      } else {
        onChangeSearchInput(`${prefix}:`)
      }
      setFocusOnSearchInput()
    }

    const handleToolbarKeydown: KeyboardEventHandler<HTMLDivElement> = (e) => {
      switch (e.key) {
        case 'ArrowUp':
          setFocusOnSearchInput()
          break
        case 'Escape':
          setFocusOnSearchInput()
          break
      }
    }

    const handleToolbarBlur: FocusEventHandler<HTMLDivElement> = (e) => {
      if (ref && typeof ref !== 'function' && ref.current) {
        ref.current.scrollLeft = 0
      }
      handleBlur(e)
    }

    return (
      <StyledToolbar
        onBlur={handleToolbarBlur}
        onKeyDown={handleToolbarKeydown}
        ref={ref}
      >
        {isOptimized ? (
          <ToolbarText>Search by</ToolbarText>
        ) : (
          <OptimizeButton
            data-testid='button-optimize'
            onClick={optimizeSearchInput}
          >
            <Icon fontSize='16px' name='bolt' />
            <span>Auto-prefix</span>{' '}
            <KeyboardShortcut>
              <kbd>shift</kbd> + <kbd>enter</kbd>
            </KeyboardShortcut>
          </OptimizeButton>
        )}

        {QUICK_PREFIXES.map((prefix, i) => (
          <BorrowerSearchQuickPrefixButton
            key={`prefix-${i}`}
            onClick={handleClickPrefix}
            prefix={prefix}
            usedPrefixes={usedPrefixes}
          />
        ))}
      </StyledToolbar>
    )
  },
)

BorrowerSearchToolbar.displayName = 'BorrowerSearchToolbar'

export default BorrowerSearchToolbar

const StyledToolbar = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  background: ${(p) => p.theme.background};
  padding: 8px 16px;
  width: 100%;
  min-width: 300px;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`
const ToolbarText = styled.span`
  margin-right: 8px;
  text-align: right;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${(p) => p.theme.labelText};
  font-size: 13px;
  font-weight: 500;
`
const OptimizeButton = styled(StyledToolbarButton)`
  display: flex;
  align-items: center;
  padding-left: 2px;
  white-space: nowrap;
  font-weight: 500;

  &:not(:disabled) {
    color: ${(p) => p.theme.highlight};
  }
`
const KeyboardShortcut = styled.span`
  display: flex;
  gap: 2px;
  align-items: center;
  margin-left: 6px;

  > kbd {
    display: block;
    border-radius: 4px;
    background: ${(p) => p.theme.highlight};
    padding: 2px 4px;
    text-transform: uppercase;
    line-height: 14px;
    color: ${(p) => p.theme.invertedText};
    font-size: 10px;
    font-weight: 600;
  }
`
